import { useState, useEffect, useMemo } from "react";
import { Image } from "antd";
import { IMAGE_NOT_FOUND } from "constants/common";
import ProductImage from "components/ProductImage";

interface ProductImageGalleryProps {
  images: string[];
  productName: string;
  productCode: string;
  isOutOfStock?: boolean;
  className?: string;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  images,
  productName,
  productCode,
  isOutOfStock = false,
  className = "",
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);

  // Handle case where there are no images
  const displayImages = useMemo(
    () => (images && images.length > 0 ? images : [IMAGE_NOT_FOUND]),
    [images]
  );

  const handleThumbnailClick = (index: number) => {
    if (index !== selectedImageIndex) {
      setImageLoading(true);
      setSelectedImageIndex(index);
    }
  };

  // Navigation functions
  const goToPrevious = () => {
    if (displayImages.length > 1) {
      setImageLoading(true);
      setSelectedImageIndex((prev) =>
        prev === 0 ? displayImages.length - 1 : prev - 1
      );
    }
  };

  const goToNext = () => {
    if (displayImages.length > 1) {
      setImageLoading(true);
      setSelectedImageIndex((prev) =>
        prev === displayImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (displayImages.length > 1) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          goToPrevious();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          goToNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [displayImages.length]);

  return (
    <div className={`w-full ${className}`}>
      {/* Main Image Display */}
      <div className="w-auto lg:w-[560px] relative mb-4">
        <div className="bg-gray-50 rounded-lg overflow-hidden">
          <Image.PreviewGroup
            preview={{
              onChange: (current) => {
                setImageLoading(true);
                setSelectedImageIndex(current);
              },
            }}
          >
            {displayImages.map((img, index) => (
              <ProductImage
                key={index}
                imageUrl={img}
                productCode={productCode}
                width={index === selectedImageIndex ? "100%" : 0}
                height={index === selectedImageIndex ? 500 : 0}
                alt={`${productName} ${index + 1}`}
                className={
                  index === selectedImageIndex
                    ? `object-contain transition-opacity duration-300 ${
                        imageLoading ? "opacity-70" : "opacity-100"
                      }`
                    : ""
                }
                style={index === selectedImageIndex ? {} : { display: "none" }}
                fallback={IMAGE_NOT_FOUND}
                onLoad={
                  index === selectedImageIndex
                    ? () => setImageLoading(false)
                    : undefined
                }
                onError={
                  index === selectedImageIndex
                    ? () => setImageLoading(false)
                    : undefined
                }
              />
            ))}
          </Image.PreviewGroup>
        </div>

        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 z-20 cursor-pointer"
              aria-label="Previous image"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 z-20 cursor-pointer"
              aria-label="Next image"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}

        {/* Out of Stock Overlay */}
        {isOutOfStock && (
          <div className="absolute w-1/3 h-1/3 bg-[#35373cba] flex items-center justify-center rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="text-white font-medium text-sm lg:text-base">
              สินค้าหมด
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail Images */}
      {displayImages.length > 1 && (
        <div className="flex gap-3 overflow-x-auto p-2 scrollbar-hide">
          {displayImages.map((image, index) => (
            <div
              key={index}
              className={`flex-shrink-0 cursor-pointer border-2 rounded-lg overflow-hidden transition-all duration-300 hover:border-primary-700 hover:shadow-md transform hover:-translate-y-1 ${
                selectedImageIndex === index
                  ? "border-primary-700 ring-2 ring-primary-200 shadow-lg scale-105"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => handleThumbnailClick(index)}
            >
              <ProductImage
                imageUrl={image}
                productCode={productCode}
                width={85}
                height={85}
                alt={`${productName} thumbnail ${index + 1}`}
                className="object-contain bg-white"
                fallback={IMAGE_NOT_FOUND}
                preview={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;
